import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useGetTopicsQuery } from '../store/topicApi';

const Home: React.FC = () => {
  const { data: topicLinks = [], error, isLoading } = useGetTopicsQuery();
  const user = useSelector((state: RootState) => state.auth.user);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="card text-center">
          <p className="text-red-500 font-semibold">Unable to load your forms. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="font-display font-extrabold text-3xl text-trident-gray-900 mb-1 tracking-tight">
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''} 👋
        </h1>
        <p className="text-sm text-trident-gray-500">
          Here are the blog topics assigned to you. Fill in the form and our AI will handle the rest.
        </p>
      </div>

      {topicLinks.length === 0 ? (
        <div className="card text-center py-16 animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-trident-orange-light flex items-center justify-center mx-auto mb-4">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
            </svg>
          </div>
          <h3 className="font-display font-bold text-xl text-trident-gray-900 mb-2">All caught up!</h3>
          <p className="text-sm text-trident-gray-500 max-w-md mx-auto">
            You have no pending forms right now. When an admin assigns you a blog topic, it will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {topicLinks.map((topic, index) => (
            <Link
              key={index}
              to={topic.link}
              className="group block bg-white rounded-2xl border border-trident-gray-100 hover:border-trident-orange/40 shadow-card hover:shadow-card-hover transition-all duration-200 p-5 animate-slide-up"
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-trident-orange-light text-trident-orange flex items-center justify-center flex-shrink-0 group-hover:bg-trident-gradient group-hover:text-white transition-all">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/>
                    <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-trident-gray-900 text-base mb-0.5 truncate">
                    {topic.title}
                  </h3>
                  <p className="text-xs text-trident-gray-500">
                    Pending your input — click to fill in the form
                  </p>
                </div>
                <svg className="text-trident-gray-400 group-hover:text-trident-orange group-hover:translate-x-1 transition-all flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Home;
